import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import * as _ from "lodash";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { useMachine } from "@/_zustand/machine";
import { useMaquinaById } from "@/hooks/useMaquinas";

/**
 * useMachineNavigation
 *
 * Centraliza la lógica de "ir a una máquina" (usada originalmente en
 * `Stores.jsx`): valida el estado de la máquina, arma el objeto de
 * credenciales de pago y navega a `store/:maquinaId`.
 *
 * INVARIANTE CRÍTICO: `goToMachine` recibe SOLO un `maquinaId` (nunca un
 * objeto máquina precargado) y hace el fetch a `useMaquinaById` él mismo.
 * Así el `maquina_id` que se usa para `setMachine` (y por lo tanto las
 * credenciales `webpay_*`/`oneclick_*` guardadas) y el de la URL de destino
 * son siempre exactamente el mismo id — nunca pueden desincronizarse.
 * No "optimices" esto pasando un objeto máquina ya cargado desde el
 * llamador: eso reabriría el riesgo de que se navegue con credenciales de
 * pago de una máquina distinta a la de la URL.
 */
export const useMachineNavigation = () => {
  const navigate = useNavigate();
  const { getCompanyRoute, companyHomePath } = useCompanyFlow();
  const { setMachine } = useMachine();
  const getMaquinaById = useMaquinaById();
  const [isNavigating, setIsNavigating] = useState(false);

  /**
   * @param {number|string} maquinaId
   * @returns {Promise<boolean>} true si navegó a la máquina, false en caso contrario.
   */
  const goToMachine = async (maquinaId) => {
    setIsNavigating(true);
    try {
      const resp = await getMaquinaById.mutateAsync(maquinaId);

      if (_.size(resp) === 0) {
        toast.error("Hay un error con esta máquina.");
        navigate(companyHomePath, { replace: false });
        return false;
      }

      const maquinaResp = resp[0];

      if (maquinaResp.estado_id !== 1) {
        toast.error("Esta máquina no se encuentra activa.");
        navigate(companyHomePath, { replace: false });
        return false;
      }

      const maquinaMap = {
        comuna_nombre: maquinaResp.comuna_nombre,
        region_nombre: maquinaResp.region_nombre,
        empresa_id: maquinaResp.empresa_id,
        empresa_nombre: maquinaResp.empresa_nombre,
        empresa_rut: "",
        maquina_direccion: maquinaResp.maquina_direccion,
        maquina_latitud: maquinaResp.maquina_latitud,
        maquina_longitud: maquinaResp.maquina_longitud,
        maquina_nombre: maquinaResp.maquina_nombre,
        maquinamodelo_icono: maquinaResp.maquinamodelo_icono,
        oneclick_apikey: maquinaResp.oneclick_apikey,
        oneclick_codigo_comercio: maquinaResp.oneclick_codigo_comercio,
        oneclick_codigo_tienda: maquinaResp.oneclick_codigo_tienda,
        webpay_apikey: maquinaResp.webpay_apikey,
        webpay_codigo_comercio: maquinaResp.webpay_codigo_comercio,
        maquina_id: maquinaResp.maquina_id,
        vende_licor: maquinaResp.vende_licor,
        maquina_telefono: maquinaResp.maquina_telefono,
      };

      setMachine(maquinaMap);

      navigate(getCompanyRoute(`store/${maquinaId}`), { replace: false });
      return true;
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Ocurrió un error al cargar la información de la máquina.");
      return false;
    } finally {
      setIsNavigating(false);
    }
  };

  return { goToMachine, isNavigating };
};
